export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API ${status}`);
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
    throw new ApiError(401, null);
  }
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: "include" });
  return handle<T>(res);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: "DELETE", credentials: "include" });
  return handle<T>(res);
}

/**
 * What to put on the screen when a request fails.
 *
 * `ApiError.message` is "API 500" — true, and useless to the person holding
 * the mouse. The server sends a `detail` sentence for the refusals a person
 * can act on ("give it a campaign asset"); prefer that, fall back to the
 * error code, and never render an empty pill.
 */
export function apiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { detail?: unknown; error?: unknown } | null;
    if (body && typeof body.detail === "string") return body.detail;
    if (body && typeof body.error === "string") {
      return body.error.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
    }
    return e.status >= 500 ? "The server could not complete that." : "That request was refused.";
  }
  return e instanceof Error && e.message ? e.message : "Something went wrong.";
}
